import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import type { JSX } from 'react'

import {
  ArrowDownIcon,
  ArrowUpIcon,
  Button,
  EmptyState,
  Link,
  NoteBlankIcon,
} from '@buffer-mono/popcorn'

import { NotesFeed, useNoteMentionsEnabled } from '~publish/components/Notes'
import { NotesUnreadDivider } from '~publish/components/Notes/NotesUnreadDivider'
import { useAccountId } from '~publish/contexts/AccountContext'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import { useNotesFeedScroll } from '~publish/hooks/notes/useNotesFeedScroll'
import type { NotesFailure, NotesResult } from '~publish/hooks/notes/useNotes'
import type {
  CollaborationNote,
  CreatedNote,
} from '~publish/hooks/notes/notesQueries'

import { PostNote } from './PostNote'
import { usePostNotesContext } from './PostNotesProvider'

import styles from './PostNotes.module.css'

const FAILURE_COPY: Record<NotesFailure, string> = {
  permissionDenied: 'You do not have permission to view notes.',
  unavailable: 'Notes are unavailable.',
  requestFailed: 'Notes could not load.',
}
const JUMP_CHIP_CLEARANCE = 48

type UnreadMarker = {
  selectionKey: string
  firstUnreadNoteId: string
  unreadCount: number
}

function RetryAction({
  onRetry,
  children,
}: {
  onRetry: () => void
  children: string
}): JSX.Element {
  return (
    <Link as="button" className={styles.retry} type="button" onClick={onRetry}>
      {children}
    </Link>
  )
}

function ReadyFeed({
  notes,
  hasNextPage,
  fetchingMore,
  pageError,
  loadMore,
  refreshing,
  warning,
  currentAccountId,
  marker,
}: Omit<Extract<NotesResult, { status: 'ready' }>, 'notes'> & {
  currentAccountId: string | null
  notes: readonly (CollaborationNote | CreatedNote)[]
  marker: UnreadMarker | null
}): JSX.Element {
  const [sentinelRef] = useInfiniteScrollPagination({
    loading: fetchingMore || refreshing || Boolean(pageError),
    hasNextPage,
    fetchMore: loadMore,
  })
  const mentionsEnabled = useNoteMentionsEnabled()

  if (notes.length === 0 && !hasNextPage) {
    return (
      <NotesFeed.Empty className={styles.centeredRegion}>
        {warning ? (
          <NotesFeed.Status>Some note details could not load.</NotesFeed.Status>
        ) : (
          <EmptyState size="small" className={styles.emptyState}>
            <EmptyState.Icon>
              <NoteBlankIcon />
            </EmptyState.Icon>
            <EmptyState.Heading as="h3">No Notes Yet</EmptyState.Heading>
            <EmptyState.Description>
              {mentionsEnabled
                ? 'Leave a note for yourself, or type @ to pull in a teammate.'
                : 'Leave a note for yourself.'}
            </EmptyState.Description>
          </EmptyState>
        )}
      </NotesFeed.Empty>
    )
  }

  const pageStatus = fetchingMore
    ? 'Loading more notes…'
    : pageError
      ? 'More notes could not load.'
      : warning
        ? 'Some note details could not load.'
        : null

  return (
    <>
      {(pageStatus || hasNextPage) && (
        <div className={styles.pageStatus} data-testid="notes-page-status">
          {pageStatus && (
            <NotesFeed.Status>
              <span>{pageStatus}</span>
              {pageError && !fetchingMore && (
                <RetryAction onRetry={() => void loadMore()}>
                  Retry loading more
                </RetryAction>
              )}
            </NotesFeed.Status>
          )}
          {hasNextPage && (
            <div
              ref={sentinelRef}
              className={styles.sentinel}
              data-testid="notes-pagination-sentinel"
              aria-hidden="true"
            />
          )}
        </div>
      )}
      <NoteList
        notes={notes}
        currentAccountId={currentAccountId}
        marker={marker}
      />
    </>
  )
}

function NoteList({
  notes,
  currentAccountId,
  marker,
}: {
  notes: readonly (CollaborationNote | CreatedNote)[]
  currentAccountId: string | null
  marker: UnreadMarker | null
}): JSX.Element {
  const { markRead, markUnread, notes: result } = usePostNotesContext()
  const firstUnreadIndex = marker
    ? notes.findIndex((note) => note.id === marker.firstUnreadNoteId)
    : -1

  return (
    <>
      <NotesFeed.List>
        {notes.map((note, index) => (
          <Fragment key={note.id}>
            {index === firstUnreadIndex &&
              marker &&
              (index > 0 ||
                (result.status === 'ready' && result.hasNextPage)) && (
                <NotesUnreadDivider count={marker.unreadCount} />
              )}
            <PostNote
              note={note}
              currentAccountId={currentAccountId}
              unreadStart={index === firstUnreadIndex}
            />
          </Fragment>
        ))}
      </NotesFeed.List>
      {(markRead.error || markUnread.error) && (
        <div role="alert" className={styles.readError}>
          {markRead.error
            ? 'Could not mark Notes as read. Try again.'
            : 'Could not mark Notes as unread. Try again.'}
        </div>
      )}
    </>
  )
}

function FeedBody({
  result,
  currentAccountId,
  notes,
  marker,
}: {
  result: NotesResult
  currentAccountId: string | null
  notes: readonly (CollaborationNote | CreatedNote)[]
  marker: UnreadMarker | null
}): JSX.Element | null {
  if (result.status === 'ready') {
    return (
      <ReadyFeed
        {...result}
        currentAccountId={currentAccountId}
        notes={notes}
        marker={marker}
      />
    )
  }
  if (result.status === 'failed') {
    return (
      <>
        {notes.length > 0 && (
          <NoteList
            notes={notes}
            currentAccountId={currentAccountId}
            marker={marker}
          />
        )}
        <NotesFeed.Empty className={styles.centeredRegion}>
          <NotesFeed.Status>
            <span>{FAILURE_COPY[result.reason]}</span>
            <RetryAction onRetry={() => void result.retry()}>
              Try again
            </RetryAction>
          </NotesFeed.Status>
        </NotesFeed.Empty>
      </>
    )
  }
  if (result.status === 'loading') {
    return (
      <>
        {notes.length > 0 && (
          <NoteList
            notes={notes}
            currentAccountId={currentAccountId}
            marker={marker}
          />
        )}
        <NotesFeed.Empty className={styles.centeredRegion}>
          <NotesFeed.Status>Loading notes…</NotesFeed.Status>
        </NotesFeed.Empty>
      </>
    )
  }
  return null
}

function JumpToUnreadChip({
  feedRef,
  notes,
  result,
  seenMarker,
  onUnreadStartVisible,
}: {
  feedRef: { current: HTMLElement | null }
  notes: readonly (CollaborationNote | CreatedNote)[]
  result: NotesResult
  seenMarker: UnreadMarker | null
  onUnreadStartVisible: (firstUnreadNoteId: string, unreadCount: number) => void
}): JSX.Element | null {
  const { readState, selectionKey } = usePostNotesContext()
  const firstUnreadNoteId = readState.state?.firstUnreadNoteId
  const unreadCount = readState.state?.unreadNoteCount ?? 0
  const hasNextPage = result.status === 'ready' && result.hasNextPage
  const [direction, setDirection] = useState<'up' | 'down' | null>(null)
  const [jumping, setJumping] = useState(false)
  const [jumpFailed, setJumpFailed] = useState(false)
  const [requestedId, setRequestedId] = useState<string | null>(null)
  const selectionRef = useRef(selectionKey)
  selectionRef.current = selectionKey

  useEffect(() => {
    setJumping(false)
    setJumpFailed(false)
    setRequestedId(null)
  }, [selectionKey])

  const findUnreadStart = useCallback(
    () =>
      feedRef.current?.querySelector<HTMLElement>(
        '[data-notes-divider], [data-notes-unread-start]',
      ),
    [feedRef],
  )
  const scrollToUnreadStart = useCallback((): boolean => {
    const feed = feedRef.current
    const unreadStart = findUnreadStart()
    if (!feed || !unreadStart) return false
    feed.scrollTo({
      top: !unreadStart.hasAttribute('data-notes-divider')
        ? 0
        : feed.scrollTop +
          unreadStart.getBoundingClientRect().top -
          feed.getBoundingClientRect().top -
          JUMP_CHIP_CLEARANCE,
      behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
    })
    return true
  }, [feedRef, findUnreadStart])
  const updateDirection = useCallback((): void => {
    const feed = feedRef.current
    if (!feed || !firstUnreadNoteId || !unreadCount) {
      setDirection(null)
      return
    }
    const unreadStart = findUnreadStart()
    if (!unreadStart) {
      setDirection(hasNextPage ? 'up' : null)
      return
    }
    const feedBounds = feed.getBoundingClientRect()
    const boundaryBounds = unreadStart.getBoundingClientRect()
    // With no divider, only the first note's top edge is the unread boundary.
    const boundaryBottom = unreadStart.hasAttribute('data-notes-divider')
      ? boundaryBounds.bottom
      : boundaryBounds.top + 1
    if (
      boundaryBottom > feedBounds.top &&
      boundaryBounds.top < feedBounds.bottom
    ) {
      onUnreadStartVisible(firstUnreadNoteId, unreadCount)
      setDirection(null)
      return
    }
    setDirection(
      feed.scrollTop > 0 &&
        boundaryBottom < feedBounds.top + JUMP_CHIP_CLEARANCE
        ? 'up'
        : boundaryBounds.top > feedBounds.bottom
          ? 'down'
          : null,
    )
  }, [
    feedRef,
    findUnreadStart,
    firstUnreadNoteId,
    hasNextPage,
    onUnreadStartVisible,
    unreadCount,
  ])

  useEffect(() => {
    const feed = feedRef.current
    feed?.addEventListener('scroll', updateDirection)
    return (): void => feed?.removeEventListener('scroll', updateDirection)
  }, [feedRef, updateDirection])

  useEffect(() => {
    const feed = feedRef.current
    if (!feed || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(updateDirection)
    observer.observe(feed)
    return (): void => observer.disconnect()
  }, [feedRef, updateDirection])

  useEffect(() => {
    if (requestedId && requestedId === firstUnreadNoteId) {
      if (scrollToUnreadStart()) setRequestedId(null)
    }
    updateDirection()
  }, [
    notes,
    requestedId,
    firstUnreadNoteId,
    scrollToUnreadStart,
    updateDirection,
  ])

  if (
    result.status !== 'ready' ||
    readState.status !== 'ready' ||
    !firstUnreadNoteId ||
    !unreadCount ||
    seenMarker?.firstUnreadNoteId === firstUnreadNoteId ||
    !direction
  ) {
    return null
  }

  const jump = async (): Promise<void> => {
    if (jumping || !selectionKey) return
    const unreadStart = findUnreadStart()
    if (unreadStart) {
      scrollToUnreadStart()
      updateDirection()
      return
    }

    setJumping(true)
    setJumpFailed(false)
    const outcome = await result.loadUntilNote(firstUnreadNoteId)
    if (selectionRef.current !== selectionKey) return
    setJumping(false)
    if (outcome === 'found') setRequestedId(firstUnreadNoteId)
    if (outcome === 'failed') setJumpFailed(true)
    if (outcome === 'missing') void readState.refresh()
  }

  return (
    <div className={styles.jumpChip}>
      <Button
        type="button"
        size="small"
        variant="primary"
        loading={jumping}
        onClick={() => void jump()}
      >
        {direction === 'up' ? <ArrowUpIcon /> : <ArrowDownIcon />}
        {jumpFailed
          ? 'Retry finding new notes'
          : `${unreadCount} new ${unreadCount === 1 ? 'note' : 'notes'}`}
      </Button>
    </div>
  )
}

export function PostNotesFeed({
  label = 'Post notes',
}: {
  /** The feed's accessible name. */
  label?: string
} = {}): JSX.Element {
  const currentAccountId = useAccountId()
  const {
    notes: result,
    create,
    readState,
    markRead,
    markUnread,
    autoReadEnabled,
    selectionKey,
  } = usePostNotesContext()
  const notes = result.status === 'ready' ? result.notes : []
  const displayNotes = [
    ...notes.toReversed(),
    ...create.confirmedNotes
      .toReversed()
      .filter((note) => !notes.some((loaded) => loaded.id === note.id)),
  ]
  const { feedRef, onScroll } = useNotesFeedScroll(
    displayNotes.map((note) => note.id),
  )
  const [seenMarker, setSeenMarker] = useState<UnreadMarker | null>(null)
  const seenMarkerRef = useRef<string | null>(null)
  const previousSelectionKey = useRef(selectionKey)
  useLayoutEffect(() => {
    if (previousSelectionKey.current === selectionKey) return
    previousSelectionKey.current = selectionKey
    seenMarkerRef.current = null
    setSeenMarker(null)
  }, [selectionKey])
  const currentMarker =
    readState.status === 'ready' &&
    readState.state?.firstUnreadNoteId &&
    readState.state.unreadNoteCount &&
    selectionKey
      ? {
          selectionKey,
          firstUnreadNoteId: readState.state.firstUnreadNoteId,
          unreadCount: readState.state.unreadNoteCount,
        }
      : null
  // Auto-read keeps its divider snapshot; manual unread uses the current state.
  const marker = !autoReadEnabled
    ? currentMarker
    : currentMarker &&
        currentMarker.firstUnreadNoteId !== seenMarker?.firstUnreadNoteId
      ? currentMarker
      : seenMarker?.selectionKey === selectionKey
        ? seenMarker
        : currentMarker
  const latestNoteId = displayNotes.at(-1)?.id
  const { pending: markReadPending, submit: submitMarkRead } = markRead
  const onUnreadStartVisible = useCallback(
    (firstUnreadNoteId: string, unreadCount: number): void => {
      if (
        !selectionKey ||
        !latestNoteId ||
        !autoReadEnabled ||
        markReadPending ||
        markUnread.pending
      )
        return
      const markerKey = JSON.stringify([selectionKey, firstUnreadNoteId])
      if (seenMarkerRef.current === markerKey) return
      seenMarkerRef.current = markerKey
      setSeenMarker({ selectionKey, firstUnreadNoteId, unreadCount })
      void submitMarkRead(latestNoteId)
    },
    [
      latestNoteId,
      autoReadEnabled,
      markReadPending,
      markUnread.pending,
      selectionKey,
      submitMarkRead,
    ],
  )

  return (
    <NotesFeed
      ref={feedRef}
      onScroll={onScroll}
      className={styles.feed}
      aria-label={label}
    >
      <JumpToUnreadChip
        feedRef={feedRef}
        notes={displayNotes}
        result={result}
        seenMarker={
          autoReadEnabled && seenMarker?.selectionKey === selectionKey
            ? seenMarker
            : null
        }
        onUnreadStartVisible={onUnreadStartVisible}
      />
      <FeedBody
        result={result}
        currentAccountId={currentAccountId}
        notes={displayNotes}
        marker={marker}
      />
      {readState.status === 'failed' && (
        <div role="alert" className={styles.readError}>
          <span>Notes read status could not update.</span>
          <RetryAction onRetry={() => void readState.retry()}>
            Retry read status
          </RetryAction>
        </div>
      )}
    </NotesFeed>
  )
}
